'use client';

import React, { useState, forwardRef, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import Link from 'next/link';
import { FEATURE_FLAG_KEYS } from 'app/shop-ui/constants';
import { getDynamicImageSource, getStaticImageSource, getS3ObjectUrl } from 'libs/url';
import { Picture } from 'app/components/Picture';
import { type BasicShopUIPropTypes, type BuyerLayoutProps } from 'shop-ui/types';
import './Logo.scss';

interface LogoProps extends BasicShopUIPropTypes, BuyerLayoutProps {
    // 自行加入其他 props
    content: string;
}

export const Logo = forwardRef<HTMLDivElement, LogoProps>((props, ref) => {
    const { className, style, styleMap, isBuyer, isEditingMode } = props;

    const [isHeaderLogo, setIsHeaderLogo] = useState(false);

    const source = useMemo(() => {
        const imageUrl = styleMap?.[FEATURE_FLAG_KEYS.IMAGE_URL] || '';

        const defaultImageUrl =
            styleMap?.[FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL] || '';

        if (imageUrl) {
            return getDynamicImageSource(imageUrl);
        }

        return getStaticImageSource(getS3ObjectUrl(defaultImageUrl));
    }, [styleMap]);

    useEffect(() => {
        setIsHeaderLogo(() => {
            const element = ref as React.RefObject<HTMLDivElement>;

            if (element?.current && typeof window !== 'undefined' && window) {
                const headerContainer = element.current.closest(
                    '[data-shop-component-header]'
                ) as HTMLElement;

                return !!headerContainer;
            }

            return false;
        });
    }, [ref]);

    useEffect(() => {
        const element = ref as React.RefObject<HTMLDivElement>;

        if (element?.current && typeof window !== 'undefined' && window) {
            const headerContainer = element.current.closest(
                '[data-shop-component-header]'
            ) as HTMLElement;

            const cell = element.current.closest(
                '[data-shop-component-grid-cell]'
            ) as HTMLElement;

            if (headerContainer && cell && !isEditingMode) {
                cell.parentElement?.classList.add('header-logo--responsive');
            }
        }
    }, [ref, isEditingMode]);

    const isPortrait = useMemo(() => {
        if (!styleMap?.[FEATURE_FLAG_KEYS.MEDIA_ASPECT_RATIO]) {
            return false;
        }

        const [width, height] =
            styleMap?.[FEATURE_FLAG_KEYS.MEDIA_ASPECT_RATIO]?.split('/') || [];

        return Number(width) / Number(height) <= 1;
    }, [styleMap]);

    const sourceHeight = useMemo(() => {
        if (!isPortrait) {
            return 0;
        }

        const [, height] =
            (isPortrait &&
                styleMap?.[FEATURE_FLAG_KEYS.MEDIA_ASPECT_RATIO]?.split('/')) ||
            [];

        return Number(height) >= 60 ? 60 : Number(height);
    }, [isPortrait, styleMap]);

    if (isBuyer) {
        return (
            <div
                className="logo-container"
                ref={ref}
                style={{
                    ...style,
                    ...(isPortrait &&
                        sourceHeight &&
                        isHeaderLogo && {
                            '--floating-logo-height': `${sourceHeight}px`,
                        }),
                }}
            >
                <div
                    className={clsx('logo', className, {
                        'logo--portrait': isPortrait && isHeaderLogo,
                    })}
                >
                    <Link href="/">
                        <Picture
                            sources={[
                                {
                                    srcSet: source.srcSet,
                                    type: source.type,
                                },
                            ]}
                            src={source.src}
                            alt="Logo"
                            draggable={false}
                        />
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div
            className="logo-container"
            ref={ref}
            style={{
                ...style,
                ...(isPortrait &&
                    sourceHeight &&
                    isHeaderLogo && {
                        '--floating-logo-height': `${sourceHeight}px`,
                    }),
            }}
        >
            <div
                className={clsx('logo', className, {
                    'logo--portrait': isPortrait && isHeaderLogo,
                })}
            >
                <Picture
                    sources={[
                        {
                            srcSet: source.srcSet,
                            type: source.type,
                        },
                    ]}
                    src={source.src}
                    alt="Logo"
                    draggable={false}
                />
            </div>
        </div>
    );
});

Logo.displayName = 'Logo';
